import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { FindingClaimsList } from '../../report/FindingClaimsList';
import { SectionShell } from '../SectionShell';
import { walkCitationText } from '../../../../lib/walkCitationText';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialBadge, type EditorialBadgeTone } from '../../report/editorial';
import { customBriefQuestionSectionId } from '../../../../lib/customBriefSections';

interface Props {
  report: SiteRagReport;
  label: string;
  sectionId?: string;
}

const MONO = 'var(--report-body-font, inherit)';

/** Confidence → editorial tone (was filled green/amber/red pills). */
const CONFIDENCE_TONE: Record<string, EditorialBadgeTone> = {
  high: 'positive',
  medium: 'warning',
  low: 'negative',
};

export const CustomBriefQuestionsSection: React.FC<Props> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const answers = report.customBriefAnswers ?? [];
  const hasData = answers.length > 0;

  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      {!hasData ? null : (
        <div className="space-y-8">
          {report.customBriefName && (
            <p className="text-sm" style={{ color: roleColor(theme, 'muted') }}>
              <span className="font-medium" style={{ color: roleColor(theme, 'ink') }}>{report.customBriefName}</span>
              {report.customBriefObjective ? ` – ${report.customBriefObjective}` : ''}
            </p>
          )}
          {(report.customBriefCounterparties || report.customBriefKeyDates) && (
            <div className="flex flex-wrap gap-4 text-xs" style={{ color: roleColor(theme, 'muted') }}>
              {report.customBriefCounterparties && (
                <span><span className="font-semibold" style={{ color: roleColor(theme, 'ink') }}>Counterparties:</span> {report.customBriefCounterparties}</span>
              )}
              {report.customBriefKeyDates && (
                <span><span className="font-semibold" style={{ color: roleColor(theme, 'ink') }}>Key dates:</span> {report.customBriefKeyDates}</span>
              )}
            </div>
          )}
          {answers.map((a, index) => (
            <article
              key={a.questionId || index}
              className="space-y-3 pb-6 last:border-0"
              style={{ borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}
            >
              <h3 className="text-base font-semibold" style={{ color: roleColor(theme, 'ink') }}>
                {index + 1}. {a.question}
              </h3>
              <div>
                <h4
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ fontFamily: MONO, color: roleColor(theme, 'muted') }}
                >
                  Findings
                </h4>
                <p className="mt-1 text-sm leading-relaxed" style={{ color: roleColor(theme, 'body') }}>
                  {walkCitationText(a.findings, report.references)}
                </p>
              </div>
              <div>
                <h4
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ fontFamily: MONO, color: roleColor(theme, 'muted') }}
                >
                  Evidence
                </h4>
                <p className="mt-1 text-sm leading-relaxed" style={{ color: roleColor(theme, 'muted') }}>
                  {walkCitationText(a.evidence, report.references)}
                </p>
              </div>
              {a.redFlags.length > 0 && (
                <FindingClaimsList
                  entries={a.redFlags}
                  references={report.references}
                  siteId={report.siteId}
                  reportId={report.id}
                  sectionId={customBriefQuestionSectionId(a.questionId)}
                  responses={report.openQuestionResponses}
                  className="space-y-4"
                />
              )}
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="text-[10px] font-semibold uppercase tracking-widest"
                  style={{ fontFamily: MONO, color: roleColor(theme, 'muted') }}
                >
                  Confidence:
                </span>
                <EditorialBadge
                  theme={theme}
                  tone={CONFIDENCE_TONE[a.confidence] ?? 'warning'}
                  variant="outline"
                >
                  {a.confidence}
                </EditorialBadge>
              </span>
            </article>
          ))}
        </div>
      )}
    </SectionShell>
  );
};
